import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { fmtCurrency, fmtDate, STATUS_TR } from "@/utils/format";
import { FileDown, X, Eye, Pencil, Ban, Trash2, GitBranch, Warehouse } from "lucide-react";
import OrderDetailsDialog from "@/components/OrderDetailsDialog";
import OrderTimelineDialog from "@/components/OrderTimelineDialog";
import PageHeader from "@/components/PageHeader";
import Pager, { usePagination } from "@/components/Pager";

const STATUS_COLORS = {
  in_progress: "bg-yellow-100 text-yellow-800 border-yellow-800",
  completed: "bg-green-100 text-green-800 border-green-800",
  shipped: "bg-[#00A650] text-white border-[#00A650]",
  cancelled: "bg-[#FF3B30] text-white border-[#FF3B30]",
  partially_cancelled: "bg-orange-100 text-orange-800 border-orange-800",
};

const trDay = (iso) => new Date(iso).toLocaleDateString("en-CA", { timeZone: "Europe/Istanbul" });

function ConversionDays({ quoteDate, orderDate }) {
  if (!quoteDate || !orderDate) return <span className="text-neutral-400">—</span>;
  const days = Math.max(0, Math.round((new Date(trDay(orderDate)) - new Date(trDay(quoteDate))) / 86400000));
  const tone = days === 0 ? "bg-[#00A84F] text-white border-[#00A84F]"
    : days <= 3 ? "bg-[#A3E4B8] text-[#0B5D2A] border-[#7FD69C]"
    : days <= 7 ? "bg-[#FFE066] text-[#6B5200] border-[#F5C800]"
    : days <= 14 ? "bg-[#FF9500] text-white border-[#FF9500]"
    : "bg-[#FF3B30] text-white border-[#FF3B30]";
  return (
    <span className={`inline-block px-2 py-0.5 text-[11px] font-mono font-bold whitespace-nowrap border ${tone}`} data-days={days}>
      {days === 0 ? "Aynı gün" : `${days} gün`}
    </span>
  );
}


export default function Orders() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const canManage = user?.role === "sales" || user?.role === "admin";
  const isPrivileged = user?.role === "admin" || user?.role === "sub_admin";
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [detailId, setDetailId] = useState(null);
  const [timelineId, setTimelineId] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const load = async () => {
    setRefreshing(true);
    try { const { data } = await api.get(`/orders?_ts=${Date.now()}`); setRows(data); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setRefreshing(false); }
  };
  useEffect(() => { load(); }, []);

  const downloadPdf = async (o) => {
    try {
      const res = await api.get(`/orders/${o.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `${o.order_number}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const downloadWarehousePdf = async (o) => {
    try {
      const res = await api.get(`/orders/${o.id}/warehouse-pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `${o.order_number}-depo.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const clearFilters = () => { setQ(""); setStatus("all"); setFrom(""); setTo(""); };

  const cancelOrder = async (o) => {
    if (!window.confirm(`Sipariş ${o.order_number} iptal edilecek. Sevk edilmemiş kalemler iptal olur, depo/üretim ekranından düşer, ayrılan stoklar iade edilir. Onaylıyor musunuz?`)) return;
    try {
      const { data } = await api.post(`/orders/${o.id}/cancel`);
      toast.success(`Sipariş iptal edildi (${data.cancelled_items} kalem)`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const deleteOrder = async (o) => {
    if (!window.confirm(`İptal edilen sipariş ${o.order_number} kalıcı olarak silinecek. Onaylıyor musunuz?`)) return;
    try {
      await api.delete(`/orders/${o.id}`);
      toast.success("Sipariş silindi");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  // Show an "UPDATED" badge for 24h after last update (but not if cancelled).
  const isRecentlyUpdated = (r) => {
    if (r.cancelled_at) return false;
    if (!r.updated_at) return false;
    const diff = Date.now() - new Date(r.updated_at).getTime();
    return diff >= 0 && diff < 24 * 60 * 60 * 1000;
  };

  const filtered = useMemo(() => {
    return rows.filter((r) => {
      if (q) {
        const s = q.toLowerCase();
        if (!(r.order_number || "").toLowerCase().includes(s) &&
            !(r.customer_name || "").toLowerCase().includes(s) &&
            !(r.quote_number || "").toLowerCase().includes(s)) return false;
      }
      if (status !== "all" && r.status !== status) return false;
      const day = (r.created_at || "").slice(0, 10);
      if (from && day < from) return false;
      if (to && day > to) return false;
      return true;
    });
  }, [rows, q, status, from, to]);

  const totalValue = filtered.reduce((s, r) => s + Number(r.total || 0), 0);
  const activeFilters = (q ? 1 : 0) + (status !== "all" ? 1 : 0) + (from ? 1 : 0) + (to ? 1 : 0);
  const { page, setPage, totalPages, paged } = usePagination(filtered, 10);

  return (
    <div className="space-y-6" data-testid="orders-page">
      <PageHeader
        kicker="Sipariş Yönetimi"
        title="Siparişler"
        subtitle="Geçmiş siparişleri filtreleyip PDF olarak indirebilirsiniz."
        onRefresh={load}
        refreshing={refreshing}
        actions={
          <div className="text-right">
            {isPrivileged && (
              <>
                <div className="text-[10px] font-mono uppercase tracking-widest text-neutral-500">Filtrelenmiş Ciro</div>
                <div className="text-2xl font-mono font-bold" data-testid="orders-total-value">{fmtCurrency(totalValue)}</div>
              </>
            )}
            <div className="text-xs font-mono text-neutral-500">{filtered.length} / {rows.length} sipariş</div>
          </div>
        }
      />

      {/* Filters */}
      <div className="bg-white border-2 border-[#09090B] p-4 grid grid-cols-1 md:grid-cols-5 gap-3 items-end">
        <div className="md:col-span-2">
          <label className="text-[10px] font-mono uppercase tracking-widest text-neutral-500">Ara</label>
          <Input
            value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Sipariş no, müşteri, teklif no…"
            className="rounded-none border-2 border-[#09090B] focus-visible:ring-0"
            data-testid="orders-search"
          />
        </div>
        <div>
          <label className="text-[10px] font-mono uppercase tracking-widest text-neutral-500">Durum</label>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="rounded-none border-2 border-[#09090B] bg-white" data-testid="orders-status-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="rounded-none border-2 border-[#09090B] bg-white">
              <SelectItem value="all">Tümü</SelectItem>
              <SelectItem value="in_progress">İşlemde</SelectItem>
              <SelectItem value="completed">Tamamlandı</SelectItem>
              <SelectItem value="shipped">Sevk Edildi</SelectItem>
              <SelectItem value="cancelled">İptal Edildi</SelectItem>
              <SelectItem value="partially_cancelled">Kısmi İptal</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-[10px] font-mono uppercase tracking-widest text-neutral-500">Başlangıç</label>
          <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)}
            className="rounded-none border-2 border-[#09090B] font-mono" data-testid="orders-from-date" />
        </div>
        <div>
          <label className="text-[10px] font-mono uppercase tracking-widest text-neutral-500">Bitiş</label>
          <Input type="date" value={to} onChange={(e) => setTo(e.target.value)}
            className="rounded-none border-2 border-[#09090B] font-mono" data-testid="orders-to-date" />
        </div>
        {activeFilters > 0 && (
          <Button
            variant="outline" size="sm"
            className="md:col-span-5 rounded-none border-2 border-[#09090B] w-fit"
            onClick={clearFilters} data-testid="orders-clear-filters"
          >
            <X size={14} className="mr-1" /> Filtreleri Temizle ({activeFilters})
          </Button>
        )}
      </div>

      <div className="bg-white border-2 border-[#09090B]">
        <Table className="[&_th]:px-1 [&_td]:px-1 2xl:[&_th]:px-1.5 2xl:[&_td]:px-1.5 [&_th]:h-10 [&_th]:text-[10px] [&_th]:leading-tight [&_td]:py-2 [&_th:first-child]:pl-3 [&_td:first-child]:pl-3 [&_th:last-child]:pr-3 [&_td:last-child]:pr-3" data-testid="orders-table">
          <TableHeader>
            <TableRow className="bg-neutral-100 border-b-2 border-[#09090B]">
              <TableHead className="font-mono uppercase">Sipariş No</TableHead>
              <TableHead className="font-mono uppercase">Sipariş Tarihi</TableHead>
              <TableHead className="font-mono uppercase">Teklif</TableHead>
              <TableHead className="font-mono uppercase">Teklif Tarihi</TableHead>
              <TableHead className="font-mono uppercase" title="Teklif ile sipariş arasında geçen gün">Dönüşüm Süresi</TableHead>
              <TableHead className="font-mono uppercase">Müşteri</TableHead>
              <TableHead className="font-mono uppercase">Ödeme</TableHead>
              <TableHead className="font-mono uppercase">Sevk</TableHead>
              <TableHead className="font-mono uppercase text-right">Ara Toplam</TableHead>
              <TableHead className="font-mono uppercase text-right">Toplam</TableHead>
              <TableHead className="font-mono uppercase">Durum</TableHead>
              <TableHead className="font-mono uppercase text-right">İşlem</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {paged.map((r) => {
              const wh = r.items.filter((i) => i.allocation === "warehouse").length;
              const pr = r.items.filter((i) => i.allocation === "production").length;
              return (
                <TableRow
                  key={r.id}
                  data-testid={`order-row-${r.id}`}
                  onClick={() => setDetailId(r.id)}
                  className="cursor-pointer hover:bg-neutral-50"
                >
                  <TableCell className="font-mono font-bold">
                    <div className="flex flex-col items-start gap-0.5">
                      <span className="whitespace-nowrap">{r.order_number}</span>
                      {r.cancelled_at && (
                        <span className="text-[9px] font-mono uppercase tracking-widest px-1.5 py-0.5 border border-[#FF3B30] text-[#FF3B30]" data-testid={`order-cancelled-badge-${r.id}`}>İPTAL</span>
                      )}
                      {isRecentlyUpdated(r) && (
                        <span className="text-[9px] font-mono uppercase tracking-widest px-1.5 py-0.5 border border-[#0055FF] text-[#0055FF] animate-pulse" data-testid={`order-updated-badge-${r.id}`}>GÜNCELLENDİ</span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-xs whitespace-nowrap" data-testid={`order-date-${r.id}`}>{fmtDate(r.created_at)}</TableCell>
                  <TableCell className="font-mono text-xs whitespace-nowrap">{r.quote_number}</TableCell>
                  <TableCell className="text-xs whitespace-nowrap text-neutral-500" data-testid={`order-quote-date-${r.id}`}>{r.quote_date ? fmtDate(r.quote_date) : "—"}</TableCell>
                  <TableCell data-testid={`order-conversion-days-${r.id}`}><ConversionDays quoteDate={r.quote_date} orderDate={r.created_at} /></TableCell>
                  <TableCell className="font-semibold text-xs min-w-[100px] max-w-[200px] break-words">{r.customer_name}</TableCell>
                  <TableCell className="font-mono text-[11px] max-w-[140px]" data-testid={`order-payment-${r.id}`}>
                    {r.payment_info
                      ? <span className="inline-block px-1.5 py-0.5 border border-neutral-400 text-neutral-700">{r.payment_info}</span>
                      : <span className="text-neutral-400">—</span>}
                  </TableCell>
                  <TableCell className="text-[10px] font-mono">
                    {wh > 0 && <span className="inline-block whitespace-nowrap px-1 py-0.5 mr-1 mb-0.5 border-2 border-[#0055FF] text-[#0055FF]">DEPO×{wh}</span>}
                    {pr > 0 && <span className="inline-block whitespace-nowrap px-1 py-0.5 border-2 border-[#FF3B30] text-[#FF3B30]">ÜRETİM×{pr}</span>}
                  </TableCell>
                  <TableCell className="text-right font-mono text-xs whitespace-nowrap text-neutral-600" data-testid={`order-subtotal-${r.id}`}>{fmtCurrency(r.subtotal)}</TableCell>
                  <TableCell className="text-right font-mono text-xs font-semibold whitespace-nowrap">{fmtCurrency(r.total)}</TableCell>
                  <TableCell>
                    <span className={`inline-block px-1.5 py-0.5 text-[10px] font-mono uppercase border whitespace-nowrap ${STATUS_COLORS[r.status] || "bg-neutral-100"}`}>
                      {STATUS_TR[r.status] || r.status}
                    </span>
                  </TableCell>
                  <TableCell className="text-right whitespace-nowrap [&_button]:h-6 [&_button]:w-6 [&_button]:p-0 [&_svg]:h-3.5 [&_svg]:w-3.5" onClick={(e) => e.stopPropagation()}>
                    <Button
                      size="sm" variant="ghost"
                      onClick={() => setDetailId(r.id)}
                      title="Detayı gör"
                      data-testid={`order-view-${r.id}`}
                    >
                      <Eye size={16} />
                    </Button>
                    <Button
                      size="sm" variant="ghost"
                      onClick={() => downloadPdf(r)}
                      title="PDF indir"
                      data-testid={`order-pdf-${r.id}`}
                    >
                      <FileDown size={16} />
                    </Button>
                    <Button
                      size="sm" variant="ghost"
                      onClick={() => downloadWarehousePdf(r)}
                      title="Depo Kağıdı (fiyatsız PDF)"
                      data-testid={`order-warehouse-pdf-${r.id}`}
                    >
                      <Warehouse size={16} />
                    </Button>
                    {canManage && !r.cancelled_at && (
                      <>
                        <Button
                          size="sm" variant="ghost"
                          onClick={() => navigate(`/siparisler/${r.id}/duzenle`)}
                          title="Düzenle"
                          data-testid={`order-edit-${r.id}`}
                        >
                          <Pencil size={16} />
                        </Button>
                        <Button
                          size="sm" variant="ghost"
                          onClick={() => cancelOrder(r)}
                          title="İptal et"
                          data-testid={`order-cancel-${r.id}`}
                        >
                          <Ban size={16} className="text-[#FF3B30]" />
                        </Button>
                      </>
                    )}
                    {canManage && r.cancelled_at && (
                      <Button
                        size="sm" variant="ghost"
                        onClick={() => deleteOrder(r)}
                        title="Kalıcı olarak sil"
                        data-testid={`order-delete-${r.id}`}
                      >
                        <Trash2 size={16} className="text-red-700" />
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
            {filtered.length === 0 && (
              <TableRow>
                <TableCell colSpan={12} className="text-center py-8 text-neutral-500">
                  {rows.length === 0 ? "Sipariş yok" : "Filtrelerle eşleşen sipariş yok"}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      <Pager page={page} setPage={setPage} totalPages={totalPages} testid="orders-pager" />

      <OrderDetailsDialog
        orderId={detailId}
        open={!!detailId}
        onOpenChange={(v) => !v && setDetailId(null)}
        canEditDueDate={true}
        onChanged={load}
      />
      <OrderTimelineDialog
        orderId={timelineId}
        open={!!timelineId}
        onOpenChange={(v) => !v && setTimelineId(null)}
      />
    </div>
  );
}
